import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import * as api from './collab';
import { DISCUSSION_POLL_MS, DOC_PAGE_PROBE_MS, REVIEW_POLL_MS, lockSignature, pollInterval } from './lockState';
import type { ChangeComment, CollabDraftContent, CommentTarget, ReviewDecisionRequest } from './collabTypes';

const invalidate = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ['vault'] });

export const useVaultVersions = (ref: string | null, includeClosed = false) =>
  useQuery({
    // includeClosed is part of the key: the two responses differ, and sharing a key would
    // serve the filtered list to a caller that asked for the closed copies.
    queryKey: ['vault', 'versions', ref, includeClosed],
    queryFn: () => api.getVersions(ref as string, includeClosed),
    enabled: ref !== null,
    staleTime: 30_000,
  });

/** The working copy incl. the review thread. ``live`` polls it while a review is open on screen
 *  so the two sides of a conversation see each other without reloading — three indexed audit
 *  reads per poll, and the interval backs off if the server is struggling. */
export const useVaultDraft = (ref: string | null, enabled: boolean, live: boolean | 'open' = false) => {
  const qc = useQueryClient();
  const base = live === 'open' ? DISCUSSION_POLL_MS : REVIEW_POLL_MS;
  const query = useQuery({
    queryKey: ['vault', 'draft', ref],
    queryFn: () => api.getDraft(ref as string),
    enabled: ref !== null && enabled,
    staleTime: live ? 0 : undefined,
    refetchInterval: live ? (q) => pollInterval(base, q.state.fetchFailureCount) : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: live ? 'always' : undefined,
  });
  // Opening the discussion reads it immediately rather than waiting out the current interval —
  // "I opened it and it was stale" is the complaint an interval alone always produces.
  useEffect(() => {
    if (live === 'open' && ref) void qc.refetchQueries({ queryKey: ['vault', 'draft', ref] });
  }, [live, ref, qc]);
  return query;
};

/** Who holds the working copy — polled every ``baseMs`` while the page is in the foreground
 *  (TanStack pauses interval refetches for hidden tabs) plus on window focus. Cheap endpoint by
 *  design (auth + a few indexed single-row reads, no content). See lockState.ts for the measured
 *  numbers behind the two intervals. */
export const useDraftLock = (ref: string | null, enabled: boolean, baseMs: number) =>
  useQuery({
    queryKey: ['vault', 'draft-lock', ref],
    queryFn: () => api.getDraftLock(ref as string),
    enabled: ref !== null && enabled,
    staleTime: 0,
    // Backs off (×2 per consecutive failure, cap 60s) so a saturated server isn't hammered by the
    // pages waiting on it; a success resets to the base interval.
    refetchInterval: (query) => pollInterval(baseMs, query.state.fetchFailureCount),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });

/** Keep the DOCUMENT page honest about the lock: poll the cheap probe and, only when its state
 *  actually changes (someone checked out / took over / unlocked / submitted), refetch the document
 *  and its versions. Without this the page kept offering "Check out to edit" after another person
 *  had checked out, until a hard refresh. */
export const useLockWatcher = (
  ref: string | null,
  baseMs: number = DOC_PAGE_PROBE_MS,
  /** Fall back to the ordinary rate after this long. A fast `baseMs` is for a person WATCHING
   *  the screen; a tab left open and visible on a second monitor is not, and would otherwise ask
   *  at that rate forever. Omitted = no settling, which is right for the document page: 15s is
   *  already the ordinary rate. See REVIEW_WAIT_SETTLE_MS. */
  settleAfterMs?: number,
) => {
  const qc = useQueryClient();
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    setSettled(false);
    if (settleAfterMs === undefined || ref === null) return;
    const timer = setTimeout(() => setSettled(true), settleAfterMs);
    return () => clearTimeout(timer);
  }, [settleAfterMs, ref]);
  const probe = useDraftLock(ref, ref !== null, settled ? DOC_PAGE_PROBE_MS : baseMs);
  const last = useRef<string>('pending');
  useEffect(() => {
    const sig = lockSignature(probe.data);
    if (sig === 'pending' || sig === last.current) return;
    const first = last.current === 'pending';
    last.current = sig;
    if (first) return; // initial answer: nothing has changed yet
    qc.invalidateQueries({ queryKey: ['vault', 'document', ref] });
    qc.invalidateQueries({ queryKey: ['vault', 'versions', ref] });
    // The comparison too. A review page open before the editor submitted has a FAILED diff
    // cached (nothing to compare yet); without this it keeps showing that failure after the
    // copy arrives, which looks exactly like the staleness this watcher was added to fix.
    qc.invalidateQueries({ queryKey: ['vault', 'draftDiff', ref] });
  }, [probe.data, qc, ref]);
};

/** The tracked changes in the open working copy, for the EDITOR's rail. No decisions — this is
 *  the same description of the file the reviewer's rail works from, so the two screens stop
 *  showing different lists of the same document (2026-10-09). */
export const useDraftChanges = (ref: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['vault', 'draftChanges', ref],
    queryFn: () => api.getDraftChanges(ref as string),
    enabled: ref !== null && enabled,
    staleTime: 15_000,
    // What picks the list up again after a save marks it stale — see useSaveDraftFile. The app
    // turns this off globally; here it is the refresh, so it is turned back on.
    refetchOnWindowFocus: true,
  });

export const useVaultDraftDiff = (ref: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['vault', 'draftDiff', ref],
    queryFn: () => api.getDraftDiff(ref as string),
    enabled: ref !== null && enabled,
  });

export function usePromoteRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, runId, reason }: { ref: string; runId: number; reason: string }) =>
      api.promoteRun(ref, runId, reason),
    onSuccess: () => invalidate(qc),
  });
}

export function useCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => api.checkoutDraft(ref),
    onSuccess: () => invalidate(qc),
  });
}

export function useTakeOver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => api.takeOverDraft(ref),
    onSuccess: () => invalidate(qc),
  });
}

export function useSaveDraftFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, file, baseUpdatedAt }: { ref: string; file: File; baseUpdatedAt: string | null }) =>
      api.saveDraftFile(ref, file, baseUpdatedAt),
    onSuccess: (data, { ref }) => {
      // The next autosave must carry THIS save's stamp. Written into the cached draft at once,
      // because waiting for the refetch below leaves a window in which the editor's own next
      // save would be refused as "stale" against its previous one.
      qc.setQueryData<CollabDraftContent | undefined>(['vault', 'draft', ref], (old) =>
        old ? { ...old, updated_at: data.updated_at } : old,
      );
      // The filename changed → re-read the draft payload AND the redline gate.
      qc.invalidateQueries({ queryKey: ['vault', 'draft', ref] });
      qc.invalidateQueries({ queryKey: ['vault', 'draftDiff', ref] });
      // And the shared change list — marked stale, NOT re-read now. The server reads that list
      // by loading the .docx and parsing every part (hundreds of ms on a picture-heavy file,
      // holding a DB connection throughout), and this page autosaves as the author types, so
      // re-reading per save put that parse on the hot path of every editor in the vault. DB
      // connections are the vault's capacity limit (QA audit, 2026-10-10).
      //
      // Stale is enough because the list is SUPPLEMENTARY: the browser's own engine shows body
      // changes live, and this adds what it cannot see — a change in a page header, a folded
      // paragraph mark — which does not move while someone types in the body. It refreshes on
      // the next focus, and the reviewer always gets the file re-read fresh at submit.
      qc.invalidateQueries({ queryKey: ['vault', 'draftChanges', ref], refetchType: 'none' });
    },
  });
}

export function useSubmitDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, reason }: { ref: string; reason: string }) => api.submitDraft(ref, reason),
    onSuccess: () => invalidate(qc),
  });
}

export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => api.discardDraft(ref),
    onSuccess: () => invalidate(qc),
  });
}

export function useApproveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, password, reason }: { ref: string; password: string; reason?: string }) =>
      api.approveDraft(ref, password, reason),
    onSuccess: () => invalidate(qc),
  });
}

export function useRejectDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ref,
      reason,
      comments,
    }: {
      ref: string;
      reason: string;
      comments?: ChangeComment[];
    }) => api.rejectDraft(ref, reason, comments),
    onSuccess: () => invalidate(qc),
  });
}

export function useCommentDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, body, target }: { ref: string; body: string; target?: CommentTarget | null }) =>
      api.commentDraft(ref, body, target ?? null),
    // The thread lives on the draft payload — re-read it so the new comment shows.
    onSuccess: (_data, { ref }) => qc.invalidateQueries({ queryKey: ['vault', 'draft', ref] }),
  });
}

export function useReplyDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, body, target }: { ref: string; body: string; target?: CommentTarget | null }) =>
      api.replyDraft(ref, body, target ?? null),
    onSuccess: (_data, { ref }) => qc.invalidateQueries({ queryKey: ['vault', 'draft', ref] }),
  });
}

/** The review round's decisions — polled gently while the page is open so a second reviewer's
 *  decisions show up, and refetched after every decision made here. */
export function useReviewDecisions(ref: string, enabled: boolean) {
  return useQuery({
    queryKey: ['vault', 'review-decisions', ref],
    queryFn: () => api.getReviewDecisions(ref),
    enabled,
    staleTime: 0,
    refetchInterval: enabled ? (query) => pollInterval(REVIEW_POLL_MS, query.state.fetchFailureCount) : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
  });
}

export function useDecideChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, revisionId, body }: { ref: string; revisionId: number; body: ReviewDecisionRequest }) =>
      api.putReviewDecision(ref, revisionId, body),
    onSuccess: (page, { ref }) => {
      qc.setQueryData(['vault', 'review-decisions', ref], page);
      // the Activity tab shows each decision
      qc.invalidateQueries({ queryKey: ['vault', 'document', ref] });
    },
  });
}
